import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import styles from './Tag.scss?inline';

export namespace Tag {
    export interface ClickEventData {
        value?: string;
        tagElement: TagComponent;
    }

    export interface EditEventData {
        tagElement: TagComponent;
        value: string;
    }
}

@customElement('app-tag')
export class TagComponent extends LitElement {
    @property({ type: String }) em_value = '';
    @property({ type: String }) em_hexColor = '';
    @property({ type: Number }) em_maxTagLength?: number;
    @property({ type: Boolean }) em_isEditable = false;
    @property({ type: Boolean }) em_showCloserButton = true;
    @property({ type: Boolean }) em_forceTrim = false;

    // PROPIEDADES PARA ESTILOS
    @property({ type: String }) em_padding?: string;
    @property({ type: String }) em_borderRadius?: string;
    @property({ type: Object }) em_styles: Record<string, string> = {};
    @property({ type: Object }) em_textStyles: Record<string, string> = {};

    @state() private isEditing = false;

    static styles = [unsafeCSS(styles)];

    private clickTimeout: ReturnType<typeof setTimeout> | null = null;
    private nClick = 0;

    connectedCallback(): void {
        super.connectedCallback();
        this.applyHexColor(this.em_hexColor);
        this.applyStyleProps();
    }

    updated(changedProperties: Map<string, unknown>): void {
        if (changedProperties.has('em_hexColor')) {
            this.applyHexColor(this.em_hexColor);
        }
        if (changedProperties.has('em_padding') || changedProperties.has('em_borderRadius') || changedProperties.has('em_styles') || changedProperties.has('em_textStyles')) {
            this.applyStyleProps();
        }
    }

    private applyStyleProps() {
        // padding
        if (this.em_padding) {
            this.style.setProperty('--tag-padding', this.em_padding);
        } else {
            this.style.removeProperty('--tag-padding');
        }
        // border radius
        if (this.em_borderRadius) {
            this.style.setProperty('--tag-border-radius', this.em_borderRadius);
        } else {
            this.style.removeProperty('--tag-border-radius');
        }
        // em_styles
        if (this.em_styles && typeof this.em_styles === 'object') {
            Object.entries(this.em_styles).forEach(([k, v]) => {
                const key = k; // .startsWith('--') ? k : `--${k}`;
                if (v == null || v === '') this.style.removeProperty(key);
                else this.style.setProperty(key, v);
            });
        }
        // em_textStyles
        if (this.em_textStyles && typeof this.em_textStyles === 'object') {
            this.applyTextStyles();
        }
    }

    private applyTextStyles() {
        const textEl = this.getTextElement();
        if (!textEl) return;
        Object.entries(this.em_textStyles || {}).forEach(([k, v]) => {
            const key = String(k);
            if (v == null || v === '') {
                textEl.style.removeProperty(key);
            } else {
                textEl.style.setProperty(key, v);
            }
        });
    }

    public get _ControlContainer(): TagComponent {
        return this;
    }

    public set _Value(value: string) {
        this.em_value = value;
    }

    public get _Value(): string {
        return this.getTextContent();
    }

    public set _HexColor(hexColor: string) {
        this.em_hexColor = hexColor;
    }

    public get _HexColor(): string | undefined {
        return this.getHexColor();
    }

    public get _IsEdditting(): boolean {
        return this.isEditing;
    }

    public _SetIsEditable(isEditable: boolean): this {
        this.em_isEditable = isEditable;
        return this;
    }

    public _EnableEditting(): this {
        void this.enableEditting();
        return this;
    }

    public _CancelEditting(): this {
        this.cancelEditting();
        return this;
    }

    public _Destroy(): this {
        this.cancelEditting();
        this.remove();
        return this;
    }

    render() {
        return html`
            <div class="tag" @click=${this.handleContainerClick}>
                <span
                    class="tag-text"
                    contenteditable=${this.isEditing ? 'true' : 'false'}
                    @keyup=${this.handleKeyUp}
                    @blur=${this.handleBlur}
                >${this.em_value}</span>
                ${this.shouldShowCloserButton()
                ? html`<button class="tag_closer" type="button" @click=${this.handleRemoveClick}><span class="tag_closer_equis">×</span></button>`
                : null}
            </div>
        `;
    }

    private shouldShowCloserButton(): boolean {
        return this.isEditing && this.em_showCloserButton;
    }

    private handleContainerClick = () => {
        this.nClick += 1;
        if (this.clickTimeout) {
            clearTimeout(this.clickTimeout);
            this.clickTimeout = null;
        }

        if (this.nClick === 1) {
            this.clickTimeout = setTimeout(() => {
                this.clickTimeout = null;
                this.nClick = 0;
                this.tagClick();
            }, 300);
            return;
        }

        if (this.nClick === 2) {
            void this.enableEditting();
        }
        this.nClick = 0;
    };

    private handleRemoveClick = (event: MouseEvent) => {
        event.stopPropagation();
        this.remove();
        this.dispatchEvent(new CustomEvent('tag-remove', {
            detail: {
                value: this.getTextContent()
            },
            bubbles: true,
            composed: true
        }));
    };

    private handleKeyUp = (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            this.cancelEditting();
            return;
        }

        this.dispatchEvent(new CustomEvent('tag-keyup', {
            detail: {
                value: this.getTextContent(),
                key: event.key
            },
            bubbles: true,
            composed: true
        }));
    };

    private handleBlur = () => {
        this.cancelEditting();
    };

    private getTextElement(): HTMLSpanElement | null {
        return this.renderRoot.querySelector('.tag-text');
    }

    private getTextContent(): string {
        const textElement = this.getTextElement();
        let textContent = textElement?.textContent || this.em_value || '';
        if (this.em_forceTrim) {
            textContent = textContent.trim();
        }
        return textContent;
    }

    private cancelEditting() {
        if (!this.isEditing) return;

        const newValue = this.fixValue(this.getTextContent());
        this.em_value = newValue;
        this.isEditing = false;

        this.dispatchEvent(new CustomEvent<Tag.EditEventData>('tag-cancel-editing', {
            detail: {
                tagElement: this,
                value: newValue
            },
            bubbles: true,
            composed: true
        }));
    }

    private async enableEditting() {
        if (!this.em_isEditable || this.isEditing) return;

        const initValue = this.getTextContent();
        this.isEditing = true;

        await this.updateComplete;

        const textElement = this.getTextElement();
        if (!textElement) return;

        const range = document.createRange();
        const selection = window.getSelection();
        range.selectNodeContents(textElement);
        range.collapse(false);
        if (selection) {
            selection.removeAllRanges();
            selection.addRange(range);
        }

        textElement.focus();

        this.dispatchEvent(new CustomEvent<Tag.EditEventData>('tag-start-editing', {
            detail: {
                tagElement: this,
                value: initValue
            },
            bubbles: true,
            composed: true
        }));
    }

    private tagClick() {
        this.dispatchEvent(new CustomEvent<Tag.ClickEventData>('tag-click', {
            detail: {
                value: this.getTextContent(),
                tagElement: this
            },
            bubbles: true,
            composed: true
        }));
    }

    private fixValue(value: string): string {
        if (this.em_maxTagLength && value.length > this.em_maxTagLength) {
            return value.substring(0, this.em_maxTagLength);
        }
        return value;
    }

    private getHexColor(): string | undefined {
        const bgColor = this.style.getPropertyValue('--tag-color-bg').trim();
        return bgColor || undefined;
    }

    private applyHexColor(hexColor: string) {
        if (
            hexColor &&
            !/^#([0-9A-F]{3,4}|[0-9A-F]{6}|[0-9A-F]{8})$/i.test(hexColor)
        ) {
            console.warn(Error('Invalid hex color format. Use #RRGGBB, #RGB, #RRGGBBAA, or #RGBA.'));
            return;
        }

        if (hexColor) {
            const hexBorderColor = this.adjustHexBrightness(hexColor, -20);
            const hexEdittingBgColor = this.adjustHexBrightness(hexColor, 12);

            this.style.setProperty('--tag-color-bg', hexColor);
            this.style.setProperty('--tag-color-border', hexBorderColor);
            this.style.setProperty('--tag-color-editting-bg', hexEdittingBgColor);
            return;
        }

        this.style.removeProperty('--tag-color-bg');
        this.style.removeProperty('--tag-color-border');
        this.style.removeProperty('--tag-color-editting-bg');
    }

    private adjustHexBrightness(hexColor: string, percent: number): string {
        if (
            hexColor &&
            !/^#([0-9A-F]{3,4}|[0-9A-F]{6}|[0-9A-F]{8})$/i.test(hexColor)
        ) {
            console.warn(Error('Invalid hex color format. Use #RRGGBB, #RGB, #RRGGBBAA, or #RGBA.'));
            return hexColor;
        }

        let color = hexColor.replace('#', '');
        let alpha = '';

        if (color.length === 3 || color.length === 4) {
            color = color.split('').map((c) => c + c).join('');
        }

        if (color.length === 8) {
            alpha = color.slice(6, 8);
            color = color.slice(0, 6);
        }

        const num = parseInt(color, 16);
        let red = (num >> 16) & 0xff;
        let green = (num >> 8) & 0xff;
        let blue = num & 0xff;

        const adjust = (channel: number) => {
            const amount = Math.round((percent / 100) * 255);
            return Math.max(0, Math.min(255, channel + amount));
        };

        red = adjust(red);
        green = adjust(green);
        blue = adjust(blue);

        let result = `#${red.toString(16).padStart(2, '0')}${green.toString(16).padStart(2, '0')}${blue.toString(16).padStart(2, '0')}`;
        if (alpha) result += alpha;
        return result;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'app-tag': TagComponent;
    }

    interface HTMLElementEventMap {
        'tag-click': CustomEvent<Tag.ClickEventData>;
        'tag-keyup': CustomEvent<{ value: string; key: string }>;
        'tag-remove': CustomEvent<{ value: string }>;
        'tag-start-editing': CustomEvent<Tag.EditEventData>;
        'tag-cancel-editing': CustomEvent<Tag.EditEventData>;
    }
}